"use client";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  HStack,
  Button,
  Text,
  Badge,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import ActiveSwitch from "@/modules/Portaria/components/ActiveSwitch";
import { ReactElement, useEffect, useState, useCallback } from "react";
import { AddIcon, EditIcon, MinusIcon } from "@chakra-ui/icons";

interface TableHeader {
  key: string;
  label: string;
  isVisible: boolean;
  width?: string;
}

interface CustomTableProps {
  headers: TableHeader[];
  data: any[];
  searchValue?: string;
  onDelete?: (selectedData: any[]) => void;
  onAdd?: () => void;
  onEdit?: (selectedData: any) => void;
  onDataChange?: (newData: any[]) => void;
  showActions?: boolean;
  addButtonLabel?: string;
  deleteButtonLabel?: string;
  isReadSwitch?: boolean;
  children?: (selecteds: any) => ReactElement;
  isItemAll?: boolean;
  onSelected?: any;
  renderCell?: (item: any, header: TableHeader) => ReactElement | null;
  maxHeight?: string;
  isIdForeing?: string;
}

const getNestedValue = (obj: any, path: string) => {
  const keys = path.split(".");
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
    obj
  );
};

const renderAddress = (value: any) => {
  if (!value) return "N/A";
  
  if (typeof value === 'string') {
    const parts = value.split('-');
    if (parts.length >= 5) {
      const [id, rua, predio, andar, apartamento] = parts;
      return (
        <HStack spacing={1} justify="center" wrap="wrap">
          <Text fontWeight={'extrabold'}>{id} -</Text>
          <Text>{rua} -</Text>
          <Text>{predio} -</Text>
          <Text>{andar} -</Text>
          <Text>{apartamento}</Text>
        </HStack>
      );
    } else {
      return (
        <Badge colorScheme="gray" variant="outline">
          {value}
        </Badge>
      );
    }
  }
  
  if (typeof value === 'object') {
    return (
      <HStack spacing={1} justify="center" wrap="wrap">
        {(value.idendereco || value.id) && (
          <Text fontWeight={'extrabold'}>{(value.idendereco || value.id)} -</Text>
        )}
        {value.rua && <Text colorScheme="blue">R{value.rua} -</Text>}
        {value.predio && <Text colorScheme="teal">P{value.predio} -</Text>}
        {value.andar && <Text colorScheme="green">A{value.andar} -</Text>}
        {value.apto && <Text colorScheme="purple">A{value.apto}</Text>}
      </HStack>
    );
  }
  
  return "N/A";
};

export default function TabelaEstoquev2({
  headers,
  data,
  searchValue = "",
  onDelete,
  onAdd,
  onEdit,
  onDataChange,
  showActions = true,
  isReadSwitch,
  children,
  isItemAll,
  onSelected,
  renderCell,
  maxHeight,
  isIdForeing,
}: CustomTableProps) {
  const [selectedRows, setSelectedRows] = useState<Set<any>>(new Set());
  const [tableData, setTableData] = useState(data);
  const [filteredData, setFilteredData] = useState(data);
  const [isDataUndefined, setIsDataUndefined] = useState<boolean>(
    data === undefined
  );

  const getItemId = useCallback(
    (item: any) => {
      if (!item) return null;
      return isIdForeing && item[isIdForeing] !== undefined
        ? item[isIdForeing]
        : item.id;
    },
    [isIdForeing]
  );

  useEffect(() => {
    setIsDataUndefined(data === undefined);
    const safeData = Array.isArray(data) ? data : [];
    setTableData(safeData);
    setFilteredData(safeData);
  }, [data]);

  useEffect(() => {
    if (searchValue.trim() === "") {
      setFilteredData(tableData);
    } else {
      const filtered = tableData.filter((item) => {
        return headers
          .filter((header) => header.isVisible && header.key !== "checkbox")
          .some((header) => {
            const value = getNestedValue(item, header.key);
            return (
              value !== null &&
              String(value).toLowerCase().includes(searchValue?.toLowerCase())
            );
          });
      });
      setFilteredData(filtered);
    }
  }, [searchValue, tableData, headers]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = tableData.map((item) =>
        isItemAll ? item : getItemId(item)
      );
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (item: any, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    const id = isItemAll ? item : getItemId(item);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSwitchChange = (id: number | string) => {
    const newData = tableData.map((item) =>
      getItemId(item) === id ? { ...item, ativo: !item.ativo } : item
    );
    setTableData(newData);
    onDataChange?.(newData);
  };

  const handleDeleteClick = () => {
    const selectedData = isItemAll
      ? Array.from(selectedRows)
      : tableData.filter((row) => selectedRows.has(getItemId(row)));
    onDelete?.(selectedData);
  };

  const getStatusColorScheme = (status: string) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === "pendente") return "gray";
    if (statusLower === "armazenado") return "green.500";
    if (statusLower === "conferido") return "#117baa";
    if (statusLower === "divergente") return "red.600";
    if (statusLower.includes("em")) return "orange";
    if (statusLower.endsWith("do")) return "green.500";
    return "gray";
  };

  const getOccupationBadgeProps = (value: boolean) => {
    if (value === true) {
      return { bg: "#0f8599", color: "white", text: "Sim" };
    } else {
      return { bg: "#6d7a81", color: "white", text: "Não" };
    }
  };

  useEffect(() => {
    if (onSelected) {
      const selectedObjects = isItemAll
        ? Array.from(selectedRows)
        : tableData.filter((row) => selectedRows.has(getItemId(row)));
      onSelected(selectedObjects);
    }
  }, [selectedRows, tableData, onSelected, isItemAll, getItemId]);

  // Função para verificar se o texto precisa ser truncado e renderizar com tooltip
  const renderTextWithTooltip = (value: string | number, maxLines = 2) => {
    if (value === null || value === undefined) return "-";
    
    const textValue = String(value);
    
    // Se o texto for muito longo (mais de aproximadamente 60 caracteres para 2 linhas)
    const shouldShowTooltip = textValue.length > 60;
    
    if (shouldShowTooltip) {
      return (
        <Tooltip
          label={textValue}
          hasArrow
          rounded="2xl"
          backgroundColor="#117baa"
          placement="top"
          maxW="300px"
        >
          <Text
            fontSize="sm"
            noOfLines={maxLines}
            lineHeight="1.2"
            textAlign="center"
            wordBreak="break-word"
            whiteSpace="normal"
          >
            {textValue}
          </Text>
        </Tooltip>
      );
    }
    
    return (
      <Text
        fontSize="sm"
        noOfLines={maxLines}
        lineHeight="1.2"
        textAlign="center"
        wordBreak="break-word"
        whiteSpace="normal"
      >
        {textValue}
      </Text>
    );
  };

  const renderCellContent = (item: any, header: TableHeader) => {
    const customCell = renderCell?.(item, header);
    if (customCell) return customCell;

    const headerKey = header.key;
    const value = getNestedValue(item, headerKey);
    

    const isAddressKey = ["enderecodestino", "enderecoorigem", "enderecoDestino", "enderecoOrigem", "endereco"].includes(headerKey);
    
    if (headerKey === "checkbox") {
      return (
        <Checkbox
          size="sm"
          isChecked={selectedRows.has(isItemAll ? item : getItemId(item))}
          onChange={(e) => handleSelectRow(item, e.target.checked)}
        />
      );
    } else if (headerKey === "ativo") {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={0}>
          <ActiveSwitch
            size="sm"
            isChecked={item.ativo}
            onChange={() => handleSwitchChange(getItemId(item))}
            isReadOnly={isReadSwitch}
          />
        </Box>
      );
    } else if (isAddressKey) {
      return renderAddress(value);
    } else if (typeof value === "boolean") {
      if (value === null) return "-";
      const badgeProps = getOccupationBadgeProps(value);
      return (
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          bg={badgeProps.bg}
          color={badgeProps.color}
          textTransform="capitalize"
          fontWeight="medium"
        >
          {badgeProps.text}
        </Badge>
      );
    } else if (headerKey === "status") {
      if (value === null) return "-";
      return (
        <HStack justifyContent="center" alignItems="center" p={0}>
          <HStack w={"45%"} justifyContent={"flex-start"}>
            <Box
              background={getStatusColorScheme(value)}
              borderRadius="full"
              px={2}
              py={2}
            />
          </HStack>
          <HStack w={"100%"} justifyContent="flex-start">
            {renderTextWithTooltip(value)}
          </HStack>
        </HStack>
      );
    } else {
      // Para todos os outros tipos de dados, usar a função de tooltip
      return renderTextWithTooltip(value);
    }
  };

  return (
    <>
      {showActions && (
        <Flex p={2} justifyContent="flex-end" alignItems="center">
          <HStack>
            <IconButton
              aria-label="Add"
              icon={<AddIcon />}
              onClick={onAdd}
              colorScheme="green"
              size="sm"
              borderRadius="full"
            />
            <IconButton
              aria-label="Remove"
              icon={<MinusIcon />}
              onClick={handleDeleteClick}
              colorScheme="red"
              size="sm"
              borderRadius="full"
            />
            {onEdit !== undefined && (
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                onClick={() => {
                  if (selectedRows.size !== 1) return;
                  const [selectedItem] = Array.from(selectedRows);
                  const idToEdit = isItemAll
                    ? getItemId(selectedItem)
                    : selectedItem;
                  onEdit?.(idToEdit);
                }}
                colorScheme="blue"
                size="sm"
                borderRadius="full"
              />
            )}
          </HStack>
        </Flex>
      )}

      {!isDataUndefined && (
        <Box
          overflow="auto"
          maxHeight={maxHeight}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
        >
          <Table variant="striped" size="sm">
            <Thead position={"sticky"} top={0} zIndex={1} bg="#0f4f6d">
              <Tr>
                {headers
                  .filter((header) => header.isVisible)
                  .map((header, index) => (
                    <Th
                      key={index}
                      color="white"
                      bg="#0f4f6d"
                      textAlign="center"
                      width={header.width}
                      p={2}
                    >
                      {header.key === "checkbox" ? (
                        <Checkbox
                          size="sm"
                          isChecked={
                            selectedRows.size === tableData.length &&
                            tableData.length > 0
                          }
                          isIndeterminate={
                            selectedRows.size > 0 &&
                            selectedRows.size < tableData.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      ) : (
                        <Text>{header.label}</Text>
                      )}
                    </Th>
                  ))}
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <Tr
                    key={getItemId(item) || index}
                    bg={
                      selectedRows.has(isItemAll ? item : getItemId(item))
                        ? "gray.100"
                        : "white"
                    }
                  >
                    {headers
                      .filter((header) => header.isVisible)
                      .map((header, idx) => (
                        <Td
                          key={`${getItemId(item)}-${header.key}-${idx}`}
                          onDoubleClick={() => onEdit?.(getItemId(item))}
                          textAlign="center"
                          maxW="200px"
                          p={2}
                        >
                          {renderCellContent(item, header)}
                        </Td>
                      ))}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td
                    colSpan={headers.filter((h) => h.isVisible).length}
                    textAlign="center"
                  >
                    Nenhum registro encontrado
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
      {isDataUndefined && (
        <Box textAlign="center" mt={4}>
          <Text color="red.500">Nenhum dado encontrado.</Text>
        </Box>
      )}

      {children?.(selectedRows || [])}
    </>
  );
}